import { RandomId } from "./RandomId";
import config from "../config";

const base_url = config.base_url;

/*
  Asset Evaluation — shared helpers for the Add Asset and Edit Asset modals.

  Nothing here touches encryption: it operates on decrypted values only, and
  Utils/dataEncryption.js encrypts whatever these produce on the way out.
*/

/* ------------------------------------------------------------------ dates */
/*
  Storage uses the app-wide YYYY/MM/DD convention (Components/User/CC Calendar
  /CCCalendar.jsx, validated by Utils/isValidDateFormat.js). The Asset
  Evaluation section uses <input type="date">, which can only read and emit
  YYYY-MM-DD. So convert at the input boundary: hyphens in the DOM, slashes in
  storage.

  Only ever called with decrypted values. Ciphertext also contains hyphens, so
  never point these at an encrypted string.
*/
export const toInputDate = (stored) =>
  stored ? String(stored).replace(/\//g, "-") : "";

export const toStoredDate = (input) =>
  input ? String(input).replace(/-/g, "/") : "";

/* -------------------------------------------------------------- exposure */
// Mirrors FINANCIAL_EXPOSURE_OPTIONS in the AssetEvaluation component.
export const BUILT_IN_EXPOSURES = [
  "crypto",
  "real_estate",
  "stocks",
  "bonds",
  "private_equity",
];

// "Real Estate" / " real estate " / "REAL_ESTATE" all normalise to real_estate.
const normaliseLabel = (label) =>
  String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

/*
  Resolves what `financial_exposure` should hold when the user picks Custom and
  types a name, creating a per-user option only when the label is genuinely new.

  Dedupe happens here rather than server-side because the stored labels are
  encrypted and the backend cannot compare them. Matching is case-insensitive on
  the trimmed label, against the built-ins first (so typing "Stocks" selects the
  built-in rather than creating a duplicate) and then the user's own options.

  Returns { financial_exposure, options } — options being the full list to
  persist on the user document.
*/
export const resolveExposureSelection = (evaluation, existingOptions = []) => {
  const options = Array.isArray(existingOptions) ? [...existingOptions] : [];
  const selected = evaluation?.financial_exposure;

  if (selected !== "custom") {
    return { financial_exposure: selected || "", options };
  }

  const typed = String(evaluation?.financial_exposure_custom_label || "").trim();
  // Custom picked but nothing typed: send neither, exactly as the spec says.
  if (!typed) return { financial_exposure: "", options };

  const key = normaliseLabel(typed);

  const builtIn = BUILT_IN_EXPOSURES.find((value) => value === key);
  if (builtIn) return { financial_exposure: builtIn, options };

  const existing = options.find(
    (option) => normaliseLabel(option?.label) === key
  );
  if (existing) return { financial_exposure: existing.value, options };

  const created = { value: `fx_${RandomId(6)}`, label: typed };
  options.push(created);
  return { financial_exposure: created.value, options };
};

/* --------------------------------------------------------------- records */
/*
  Records the evaluation, and the debt when one was entered.

  A date identifies a reading. Saving a date that already has a record REPLACES
  that record rather than adding a second one, so the chart keeps one point per
  date and correcting a figure does not leave the old one behind. Debts follow
  the same rule against their own payoff dates.

  Nothing is appended when the panel was not filled in — no date and no value
  means no reading — which is what makes the blank-on-open panel safe.

  A debt record is dated when it was inserted, so both series sit on the same
  timeline. The payoff date describes the debt, not a reading, and lives on the
  asset.
*/
const isFilled = (value) =>
  value !== "" && value !== undefined && value !== null;

// Replaces the record carrying `date`, or appends when the date is new.
const upsertRecord = (records, entry) => {
  const index = records.findIndex(
    (record) => toStoredDate(record?.date) === entry.date
  );
  if (index < 0) return [...records, entry];
  const next = [...records];
  // Keep the original id and created_at: this is the same reading, corrected.
  next[index] = {
    ...entry,
    id: records[index].id,
    created_at: records[index].created_at ?? entry.created_at,
  };
  return next;
};

export const appendEvaluationRecords = (asset, evaluation) => {
  let records = Array.isArray(asset?.asset_evaluation_records)
    ? [...asset.asset_evaluation_records]
    : [];
  let debtRecords = Array.isArray(asset?.asset_debt_records)
    ? [...asset.asset_debt_records]
    : [];

  const date = toStoredDate(evaluation?.evaluation_date);
  const value = evaluation?.asset_value;
  const now = Date.now();

  if (date && isFilled(value)) {
    records = upsertRecord(records, {
      id: RandomId(6),
      date,
      value: Number(value),
      currency: evaluation?.currency || "",
      created_at: now,
    });
  }

  /*
    A debt record is dated when it was INSERTED, not by the payoff date. The
    payoff date describes the debt itself — there is one of them, held on the
    asset — whereas a record is a reading of what is owed at a point in time,
    exactly like a valuation. Two readings on the same day replace each other,
    as they do on the evaluation series.
  */
  const debtAmount = evaluation?.debt_amount;
  if (evaluation?.has_associated_debt === true && isFilled(debtAmount)) {
    debtRecords = upsertRecord(debtRecords, {
      id: RandomId(6),
      // A debt is a liability and is always stored negative, so it plots below
      // the zero line however the field was filled in.
      value: -Math.abs(Number(debtAmount)),
      date: todayStored(),
      currency: evaluation?.debt_currency || evaluation?.currency || "",
      created_at: now,
    });
  }

  return {
    asset_evaluation_records: records,
    asset_debt_records: debtRecords,
  };
};

/*
  Keeps only the field set that matches an entry's `type`.

  The panel deliberately holds BOTH sets while it is open, so flipping the type
  back and forth does not throw away what you already typed. The moment the
  entry is saved that stops being helpful: an entry switched from
  "other owners" to "custom" would otherwise carry its old owners, and vice
  versa. encryptDataEntries already drops the unused half on the way to the
  database, but normalising here means the in-memory asset matches what is
  stored, so nothing reading UserData sees a set the entry no longer has.
*/
/*
  An entry only ever carries the fields its own type uses, and every type
  carries the same shape whether or not it uses them — so a saved entry has no
  leftovers from a type it used to be, and nothing downstream has to guess which
  fields are meaningful.
*/
const EMPTY_ENTRY = {
  my_ownership: "",
  owners: [],
  custom_fields: [],
  country: "",
  amount: "",
  currency: "",
};

export const normaliseDataEntries = (entries) =>
  (Array.isArray(entries) ? entries : []).map((entry) => {
    const base = { ...EMPTY_ENTRY, id: entry?.id };
    switch (entry?.type) {
      case "other_owners":
        return {
          ...base,
          type: "other_owners",
          my_ownership: entry?.my_ownership ?? "",
          owners: Array.isArray(entry?.owners) ? entry.owners : [],
        };
      case "location":
        return { ...base, type: "location", country: entry?.country ?? "" };
      case "annual_income":
      case "monthly_income":
        return {
          ...base,
          type: entry.type,
          amount: entry?.amount ?? "",
          currency: entry?.currency ?? "",
        };
      default:
        return {
          ...base,
          type: "custom",
          custom_fields: Array.isArray(entry?.custom_fields)
            ? entry.custom_fields
            : [],
        };
    }
  });

/*
  Folds the evaluation panel into the asset being saved, and returns the user's
  exposure options alongside it since a new custom type may have been created.

  Because the panel reopens blank, a blank field means "the user did not enter
  anything this time" — NOT "clear what was there". So each asset-level value is
  only overwritten when the form actually carries one; otherwise the stored
  value is kept. Without this, opening an asset and pressing Save would wipe its
  financial exposure, its value and its debt.

  The two exceptions are the fields that DO reopen prefilled, and therefore
  always carry the user's current intent: is_liquid and data_entries.
*/
const keepUnlessProvided = (provided, stored) =>
  isFilled(provided) ? provided : stored ?? "";

export const mergeEvaluationIntoAsset = (
  asset,
  evaluation,
  exposureOptions,
  /*
    The owner lists, optional so existing callers keep working. A row whose
    owner is "Custom" with a name typed beside it becomes that name here, and
    the name joins the user's saved owners — the same round trip the exposure
    above makes.
  */
  ownerCustomOptions = [],
  knownOwnerNames = []
) => {
  const { financial_exposure, options } = resolveExposureSelection(
    evaluation,
    exposureOptions
  );

  let ownerOptions = Array.isArray(ownerCustomOptions)
    ? [...ownerCustomOptions]
    : [];
  const dataEntries = normaliseDataEntries(evaluation?.data_entries).map(
    (entry) => {
      if (entry.type !== "other_owners") return entry;
      const resolved = resolveOwnersSelection(
        entry.owners,
        ownerOptions,
        knownOwnerNames
      );
      ownerOptions = resolved.options;
      return { ...entry, owners: resolved.owners };
    }
  );
  const { asset_evaluation_records, asset_debt_records } =
    appendEvaluationRecords(asset, evaluation);

  const enteredDate = toStoredDate(evaluation?.evaluation_date);
  const debtOn = evaluation?.has_associated_debt === true;

  return {
    asset: {
      ...asset,
      /*
        Taken directly, not kept-unless-provided like the reading fields: the
        exposure reopens prefilled, so whatever the field holds is deliberate —
        including blank, which clears it. Clearing is only reachable while the
        asset has no readings; once one exists, validate() refuses a blank
        exposure, because a reading cannot stand without one.
      */
      financial_exposure,
      evaluation_date: keepUnlessProvided(enteredDate, asset?.evaluation_date),
      asset_value: keepUnlessProvided(evaluation?.asset_value, asset?.asset_value),
      currency: keepUnlessProvided(evaluation?.currency, asset?.currency),
      // Prefilled in the panel, so the switch position is always intentional.
      is_liquid: evaluation?.is_liquid === true,
      /*
        The debt DESCRIPTION — type, payoff date, annual interest — describes
        the debt itself, one of each per asset, and reopens prefilled. So it is
        taken directly from the panel, the same way the exposure is: whatever
        the fields hold is deliberate.

        Turning the switch off clears the description outright. That is the only
        way to remove a debt, and the switch is disabled while debt records
        exist, so it cannot be reached once there is history to orphan.
      */
      has_associated_debt: debtOn,
      debt_type: debtOn ? evaluation?.debt_type || "" : "",
      debt_payoff_date: debtOn ? toStoredDate(evaluation?.debt_payoff_date) : "",
      debt_annual_interest: debtOn ? evaluation?.debt_annual_interest ?? "" : "",
      /*
        The debt READING. Blank means "not entered this time" rather than
        "clear it" — these reopen empty so a new amount can be recorded.
      */
      debt_amount: debtOn
        ? keepUnlessProvided(evaluation?.debt_amount, asset?.debt_amount)
        : "",
      debt_currency: debtOn
        ? keepUnlessProvided(evaluation?.debt_currency, asset?.debt_currency)
        : "",
      /*
        Prefilled and edited in place, so the panel is the source of truth —
        normalised so an entry only carries the set matching its type.
      */
      data_entries: dataEntries,
      asset_evaluation_records,
      asset_debt_records,
    },
    exposureOptions: options,
    ownerOptions,
  };
};

/*
  Folds ONLY the evaluation reading into the asset: the exposure, the date, the
  value, the currency, and the record those make.

  This is what the Save button under the currency writes. It exists next to
  mergeEvaluationIntoAsset rather than reusing it because that one folds in the
  whole panel — the debt block, the ownership rows, the liquid switch — and this
  button validated none of those. A half-typed debt sitting in the form when the
  user records a valuation must not be written to the asset on the strength of
  it, so the fields it did not check, it does not touch.

  The rest is deliberately identical: the same exposure resolution, so a newly
  typed custom type is created once and reused; the same appendEvaluationRecords,
  so a second reading on a date that already has one replaces it rather than
  stacking. Pressing this button and then pressing the asset's own Save must not
  produce two different results.
*/
export const mergeReadingIntoAsset = (asset, evaluation, exposureOptions) => {
  const { financial_exposure, options } = resolveExposureSelection(
    evaluation,
    exposureOptions
  );
  /*
    Passed a reading-only evaluation, so no debt record can be appended from it
    however the debt fields happen to be filled in at the time.
  */
  const { asset_evaluation_records } = appendEvaluationRecords(asset, {
    evaluation_date: evaluation?.evaluation_date,
    asset_value: evaluation?.asset_value,
    currency: evaluation?.currency,
  });
  const enteredDate = toStoredDate(evaluation?.evaluation_date);

  return {
    asset: {
      ...asset,
      financial_exposure,
      evaluation_date: keepUnlessProvided(enteredDate, asset?.evaluation_date),
      asset_value: keepUnlessProvided(
        evaluation?.asset_value,
        asset?.asset_value
      ),
      currency: keepUnlessProvided(evaluation?.currency, asset?.currency),
      asset_evaluation_records,
    },
    exposureOptions: options,
  };
};

/*
  The debt block's own Save, the way mergeReadingIntoAsset is the reading's.

  Only the debt is written. The reading fields and the ownership rows are left
  exactly as the asset already has them, so pressing this cannot save a
  half-typed valuation on the back of a debt -- the mirror of the rule that
  button follows in the other direction.

  `asset_debt_records` gains a row the same way it would on a full save: dated
  today, because a debt record is dated when it was INSERTED rather than by the
  payoff date, and stored negative because a liability plots below the line.
  appendEvaluationRecords is given a debt-only evaluation, so no valuation
  record can be appended from it however the reading fields happen to be filled
  in at the time.
*/
/*
  One "New Field" block's own Save.

  Only that block is written. The asset's other blocks are taken from the asset
  as it already stands, not from the panel, so saving one field cannot carry a
  half-typed neighbour in with it -- the same rule the reading and the debt
  follow with their own buttons.

  `other_owners` carries custom owner names the user may have just typed, so
  the merged entry goes through normaliseDataEntries the way a full save does.
*/
export const mergeEntryIntoAsset = (
  asset,
  evaluation,
  entryId,
  ownerOptions,
  knownOwnerNames
) => {
  const staged = normaliseDataEntries(evaluation?.data_entries);
  const found = staged.find((item) => item.id === entryId);
  if (!found) return { asset, ownerOptions };

  /*
    A newly typed owner name becomes one of the user's own owner options, the
    same as it would on a full save -- otherwise the name would be stored on
    the entry and forgotten everywhere else.
  */
  let options = ownerOptions;
  let entry = found;
  if (found.type === "other_owners") {
    const resolved = resolveOwnersSelection(
      found.owners,
      options,
      knownOwnerNames
    );
    options = resolved.options;
    entry = { ...found, owners: resolved.owners };
  }

  const existing = normaliseDataEntries(asset?.data_entries);
  const known = existing.some((item) => item.id === entryId);
  const data_entries = known
    ? existing.map((item) => (item.id === entryId ? entry : item))
    : [...existing, entry];

  return { asset: { ...asset, data_entries }, ownerOptions: options };
};

export const mergeDebtIntoAsset = (asset, evaluation) => {
  const debtOn = evaluation?.has_associated_debt === true;
  const { asset_debt_records } = appendEvaluationRecords(asset, {
    has_associated_debt: evaluation?.has_associated_debt,
    debt_amount: evaluation?.debt_amount,
    debt_currency: evaluation?.debt_currency,
  });

  /*
    Cleared rather than kept when the toggle is off, the same as the full
    merge does: the toggle IS the claim that a debt exists, so turning it off
    has to take the figures with it or the asset keeps a debt it no longer
    says it has.
  */
  return {
    ...asset,
    has_associated_debt: debtOn,
    debt_type: debtOn ? evaluation?.debt_type || "" : "",
    debt_payoff_date: debtOn ? toStoredDate(evaluation?.debt_payoff_date) : "",
    debt_annual_interest: debtOn ? evaluation?.debt_annual_interest ?? "" : "",
    debt_amount: debtOn
      ? keepUnlessProvided(evaluation?.debt_amount, asset?.debt_amount)
      : "",
    debt_currency: debtOn
      ? keepUnlessProvided(evaluation?.debt_currency, asset?.debt_currency)
      : "",
    asset_debt_records,
  };
};

/*
  Rebuilds the panel's state when the Edit Asset modal opens.

  The READING fields — date, value and currency — open EMPTY even when the asset
  has values saved. The panel is a form for entering the next reading, so
  prefilling last time's numbers would invite the user to save the same figures
  again as though a fresh valuation had been taken. Previous readings stay
  visible in the chart and the history tables.

  Everything that describes the ASSET rather than a reading carries over,
  because there is only ever one of each per asset:
    - financial_exposure (with its custom label), which the user sets once
    - is_liquid, a standing property
    - data_entries, the owners and custom-field blocks, edited in place
*/
export const evaluationFromAsset = (asset, customOptions = []) => {
  /*
    Kept exactly as stored, custom types included.

    A custom type is already an option in the dropdown — the panel renders the
    user's saved list after the built-ins — so the stored id selects it the same
    way a built-in does. Mapping it back to "custom" instead reopened the modal
    on the free-text field with the name retyped in it, which read as a second,
    unsaved entry rather than the choice that was made.

    `customOptions` is no longer needed for this, and is kept only so existing
    callers do not have to change.
  */
  const exposure = asset?.financial_exposure || "";

  return {
    financial_exposure: exposure,
    financial_exposure_custom_label: "",
    evaluation_date: "",
    asset_value: "",
    currency: "",
    is_liquid: asset?.is_liquid === true,
    /*
      The debt DESCRIPTION carries over — one type, one payoff date, one
      interest rate per asset — and the switch reflects whether the asset has a
      debt at all, or those fields would sit hidden behind an off toggle. The
      debt READING opens blank, like the valuation reading, so a new amount can
      be recorded.
    */
    has_associated_debt: asset?.has_associated_debt === true,
    debt_type: asset?.debt_type || "",
    debt_payoff_date: toInputDate(asset?.debt_payoff_date),
    debt_annual_interest: asset?.debt_annual_interest ?? "",
    debt_amount: "",
    debt_currency: "",
    data_entries: Array.isArray(asset?.data_entries) ? asset.data_entries : [],
  };
};

/* -------------------------------------------------------------- currency */
/*
  USD-based rate table from the backend. Same data the referral flows already
  use — see POST /user/currency/rate in Routes/User.js, which reads the file
  getExchangeRates() refreshes daily.
*/
export const fetchCurrencyRates = () => {
  return fetch(base_url + "user/currency/rate", {
    method: "POST",
    mode: "cors",
    headers: {
      Accept: "application/json, text/plain, */*",
      "Content-type": "application/x-www-form-urlencoded",
      credentials: "include",
    },
    credentials: "include",
  }).then((res) => {
    if (res.status !== 200) throw new Error(`Rates unavailable (${res.status})`);
    return res.json();
  });
};

/*
  Converts between two currencies via the USD base. Returns the amount
  unchanged when either rate is missing, so a currency the table does not cover
  degrades to "shown as entered" instead of rendering NaN.
*/
export const convertAmount = (amount, fromCurrency, toCurrency, rates) => {
  const value = Number(amount);
  if (Number.isNaN(value)) return 0;
  if (!rates || !fromCurrency || !toCurrency) return value;

  const from = String(fromCurrency).toLowerCase();
  const to = String(toCurrency).toLowerCase();
  if (from === to) return value;

  const fromRate = from === "usd" ? 1 : rates[from];
  const toRate = to === "usd" ? 1 : rates[to];
  if (!fromRate || !toRate) return value;

  return (value / fromRate) * toRate;
};

/* ------------------------------------------------- latest record helpers */
/*
  A record's date as a sortable number. Stored dates are YYYY/MM/DD — the
  `<input type="date">` value with its dashes swapped — so the digits alone
  already sort correctly; pulling them out rather than comparing the strings
  keeps a stray separator or a single-digit month from changing the answer.

  Undated records sort to the front, where they cannot be mistaken for the
  newest reading.
*/
const recordDateKey = (record) => {
  const parts = String(record?.date || "").match(/\d+/g);
  if (!parts || parts.length < 3) return -Infinity;
  const [year, month, day] = parts;
  return Number(year) * 10000 + Number(month) * 100 + Number(day);
};

/*
  Records oldest first, by the date on the reading rather than the order it was
  typed in.

  The two are not the same: a valuation entered today can be dated last year,
  and until now that reading landed at the end of the history and counted as the
  newest one. Sorting by date puts every series — the tables and the chart drawn
  from them — in the order the readings actually happened.

  Ties fall back to when the record was created, then to the order it already
  had, so two readings that genuinely share a date stay put rather than
  swapping about between renders. (`upsertRecord` replaces a same-date reading
  in a series, so this mostly matters for older data.)
*/
export const sortRecordsByDate = (records) =>
  (Array.isArray(records) ? records : [])
    .map((record, index) => ({ record, index }))
    .sort((a, b) => {
      const byDate = recordDateKey(a.record) - recordDateKey(b.record);
      if (byDate !== 0) return byDate;
      const byCreated =
        Number(a.record?.created_at || 0) - Number(b.record?.created_at || 0);
      if (byCreated !== 0) return byCreated;
      return a.index - b.index;
    })
    .map((entry) => entry.record);

/*
  Both asset tables show the value of the most recent record — most recent BY
  DATE, not the last one added. Entering a valuation dated in the past used to
  overwrite the column with that older figure simply because it was typed last;
  now the newest reading is the one shown whenever it was entered. Delete every
  record and the column empties, which is the intended behaviour.
*/
export const latestRecord = (records) => {
  const sorted = sortRecordsByDate(records);
  return sorted.length ? sorted[sorted.length - 1] : null;
};

// "" rather than 0 when there is nothing, so the tables can show their dash.
export const latestValueOf = (asset) => {
  const record = latestRecord(asset?.asset_evaluation_records);
  return record && isFilled(record.value) ? record.value : "";
};

export const latestDebtOf = (asset) => {
  const record = latestRecord(asset?.asset_debt_records);
  return record && isFilled(record.value) ? record.value : "";
};

// The currency the latest value was recorded in, for labelling the column.
export const latestCurrencyOf = (asset) => {
  const record = latestRecord(asset?.asset_evaluation_records);
  return record?.currency || asset?.currency || "";
};

export const latestDebtCurrencyOf = (asset) => {
  const record = latestRecord(asset?.asset_debt_records);
  return record?.currency || asset?.debt_currency || asset?.currency || "";
};

// Today in storage form (YYYY/MM/DD), for records created from the table.
export const todayStored = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}/${pad(now.getMonth() + 1)}/${pad(now.getDate())}`;
};

/*
  Inline value edit from the Asset Management table.

  Editing the Asset Value cell records a new valuation dated today, carrying the
  currency forward from the previous record — the asset's exposure and the rest
  of its evaluation data are asset-level and simply stay as they are.

  If today already has a record, its value is REPLACED rather than a second one
  added. That is the one place the "one evaluation per date" rule resolves by
  overwriting instead of erroring, because the user is editing a value in place
  rather than entering a new reading.

  Debt behaves differently by necessity: a debt record is keyed by its payoff
  date, and the table has no payoff date to offer. So editing the Debt Value
  cell updates the most recent debt record in place, keeping its payoff date. If
  the asset has no debt record yet, there is nothing to date and the edit is
  ignored — a debt has to be created in the modal, where a payoff date is asked
  for.
*/
export const applyInlineValueEdit = (asset, patch, fallbackCurrency = "") => {
  const records = Array.isArray(asset?.asset_evaluation_records)
    ? [...asset.asset_evaluation_records]
    : [];
  const debtRecords = Array.isArray(asset?.asset_debt_records)
    ? [...asset.asset_debt_records]
    : [];
  const today = todayStored();
  const now = Date.now();

  if (isFilled(patch?.asset_value)) {
    const previous = latestRecord(records);
    const existingToday = records.findIndex(
      (record) => toStoredDate(record?.date) === today
    );
    const entry = {
      id: existingToday >= 0 ? records[existingToday].id : RandomId(6),
      date: today,
      value: Number(patch.asset_value),
      /*
        The currency of the previous reading, or the asset's, or — for a first
        record created from the table, where there is neither — the language
        default the panel would have used ($ in English, € in French).
      */
      currency: previous?.currency || asset?.currency || fallbackCurrency,
      created_at: existingToday >= 0 ? records[existingToday].created_at : now,
    };
    if (existingToday >= 0) records[existingToday] = entry;
    else records.push(entry);
  }

  /*
    A debt edit from the table records a new reading dated today, exactly as an
    asset-value edit does, replacing today's if one already exists. It carries
    the previous reading's currency, or the language default when there is none.
  */
  if (isFilled(patch?.debt_amount)) {
    const previousDebt = latestRecord(debtRecords);
    const existingToday = debtRecords.findIndex(
      (record) => toStoredDate(record?.date) === today
    );
    const entry = {
      id: existingToday >= 0 ? debtRecords[existingToday].id : RandomId(6),
      date: today,
      value: -Math.abs(Number(patch.debt_amount)),
      currency:
        previousDebt?.currency || asset?.debt_currency || fallbackCurrency,
      created_at:
        existingToday >= 0 ? debtRecords[existingToday].created_at : now,
    };
    if (existingToday >= 0) debtRecords[existingToday] = entry;
    else debtRecords.push(entry);
  }

  return {
    ...asset,
    asset_evaluation_records: records,
    asset_debt_records: debtRecords,
  };
};

/* ---------------------------------------------------------- number display */
/*
  665000 -> "665,000". Display only: the callers strip every non-digit on the
  way back into state, so the separator never reaches storage. Shared so the
  Asset Evaluation panel and the Asset Management table format identically.
*/
export const displayAmount = (raw) => {
  if (raw === "" || raw === "-" || raw === undefined || raw === null) {
    return raw === undefined || raw === null ? "" : raw;
  }
  const negative = String(raw).startsWith("-");
  const digits = String(raw).replace(/-/g, "");
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "-" : ""}${grouped}`;
};

/* ------------------------------------------------------- currency symbols */
export const CURRENCY_SYMBOLS = {
  eur: "€",
  usd: "$",
  gbp: "£",
  chf: "CHF",
  cad: "CA$",
  aud: "A$",
  jpy: "¥",
  cny: "CN¥",
};

/*
  Where the symbol goes. The euro and the franc trail the amount ("15K €"),
  every other currency here leads it ("$15K") — writing "15K$" is simply wrong
  in English, and "€15K" reads as wrong in French.

  A negative sign always comes first, so a debt is "-$120K", never "$-120K".
*/
const SUFFIX_CURRENCIES = new Set(["eur", "chf"]);

export const withCurrencySymbol = (body, negative, currencyCode) => {
  const code = String(currencyCode || "").toLowerCase();
  const symbol = CURRENCY_SYMBOLS[code] || "";
  const sign = negative ? "-" : "";
  if (!symbol) return `${sign}${body}`;
  return SUFFIX_CURRENCIES.has(code)
    ? `${sign}${body} ${symbol}`
    : `${sign}${symbol}${body}`;
};

/*
  Compact form: 3460000 -> "$3.46M", -120000 in euros -> "-120K €". Returns
  null for anything unusable so callers can show a dash.

  `decimals` is how far the compacted number is rounded. The two asset tables
  keep the default 2; the chart and the evaluation history tables pass 1, so a
  converted amount reads "$28.1K" beside the chart point it belongs to instead
  of "$28,129.114". Trailing zeros are dropped either way, so a round number
  stays "665K" rather than "665.0K".
*/
export const formatCompactMoney = (value, currencyCode, decimals = 2) => {
  if (value === null || value === undefined || value === "") return null;
  const amount = Number(value);
  if (Number.isNaN(amount)) return null;
  const factor = Math.pow(10, decimals);
  const trim = (n) => String(Math.round(n * factor) / factor);
  const abs = Math.abs(amount);
  let compact;
  if (abs >= 1000000) compact = `${trim(abs / 1000000)}M`;
  else if (abs >= 1000) compact = `${trim(abs / 1000)}K`;
  else compact = trim(abs);
  return withCurrencySymbol(compact, amount < 0, currencyCode);
};

/* ------------------------------------------------------------ ownership */
/*
  The three asset types that can hold an ownership stake, lowercased for
  comparison. An asset of any of these can be named as another asset's owner.
*/
export const OWNER_COMPANY_TYPES = [
  "company",
  "company shares",
  "business ownership",
];

/*
  Everyone this user can name as an owner: their beneficiaries, and their own
  company-shaped assets.

  Plain NAMES, not references — the same trade the Add / Edit Asset panel makes.
  Renaming a beneficiary will not update rows that already name them; in
  exchange, deleting one does not leave an unresolvable row behind. The list is
  a record of who held what at the time.

  `excludeAssetId` drops the asset being edited: a company cannot be its own
  other owner, and offering it invites a row that means nothing.
*/
export const ownerOptionsFrom = (root, excludeAssetId) => {
  const data = root || {};
  const beneficiaries = (data.beneficiaries || []).map((person) =>
    [person?.name, person?.last_name].filter(Boolean).join(" ").trim()
  );
  /*
    And the owners this user has typed in themselves. Held per user the way
    custom asset types and custom exposures are, so a name entered once is
    offered on every asset afterwards instead of being retyped.
  */
  const own = (data.asset_owner_custom_options || []).map((option) =>
    String(option?.label || "").trim()
  );
  const idOf = (asset) => (asset?.id != null ? asset.id : asset?.asset_id);
  const companies = (data.assets || [])
    .filter(
      (asset) =>
        OWNER_COMPANY_TYPES.includes(
          String(asset?.asset_type || "").trim().toLowerCase()
        ) &&
        !(excludeAssetId !== undefined && idOf(asset) === excludeAssetId)
    )
    .map((asset) => String(asset?.asset_name || "").trim());
  /*
    Deduped and blanks dropped: two people with the same name would otherwise
    produce two identical options, only one of which could ever be picked.
  */
  return Array.from(
    new Set([...beneficiaries, ...companies, ...own])
  ).filter(Boolean);
};

// The sentinel the owner dropdown uses to reveal its free-text field.
export const OWNER_CUSTOM = "custom";

/*
  Resolves what an owner row should hold when "Custom" is picked and a name is
  typed, creating a per-user option only when the name is genuinely new.

  Like a custom asset type, the row stores the NAME rather than an id: the whole
  block is a record of who held what, read directly, and an id would have to be
  resolved everywhere a name is shown. The saved option carries an opaque
  `value` purely as its own key — unlike the asset-type list, the label is not
  repeated there, because only the label is encrypted and putting the name in
  `value` too would store it in clear.

  `knownNames` are the beneficiaries and company assets already on offer, so
  typing one of those selects it instead of adding a duplicate custom entry.

  Returns { owner, options }, options being the full list to persist.
*/
export const resolveOwnerSelection = (
  selected,
  typedLabel,
  existingOptions = [],
  knownNames = []
) => {
  const options = Array.isArray(existingOptions) ? [...existingOptions] : [];
  if (selected !== OWNER_CUSTOM) return { owner: selected, options };

  const typed = String(typedLabel || "").trim();
  /*
    Custom picked but nothing typed: left exactly as it was. Rows saved before
    the free-text field existed hold the bare sentinel, and rewriting those to
    blank here would fail the "every row needs an owner" rule on a block the
    user has not touched.
  */
  if (!typed) return { owner: OWNER_CUSTOM, options };

  const key = typed.toLowerCase();

  const known = (knownNames || []).find(
    (name) => String(name || "").trim().toLowerCase() === key
  );
  if (known) return { owner: known, options };

  const existing = options.find(
    (option) => String(option?.label || "").trim().toLowerCase() === key
  );
  if (existing) return { owner: existing.label, options };

  options.push({ value: `ow_${RandomId(6)}`, label: typed });
  return { owner: typed, options };
};

/*
  The same over a whole owners block, threading the growing option list through
  each row so two rows naming the same new owner add one option, not two. Drops
  the transient `owner_custom_label` the input binds to — it exists only between
  typing and saving, and must not reach the stored asset.
*/
export const resolveOwnersSelection = (
  owners,
  existingOptions = [],
  knownNames = []
) => {
  let options = Array.isArray(existingOptions) ? [...existingOptions] : [];
  const resolved = (owners || []).map((row) => {
    const result = resolveOwnerSelection(
      row?.owner,
      row?.owner_custom_label,
      options,
      knownNames
    );
    options = result.options;
    const { owner_custom_label, ...rest } = row || {};
    return { ...rest, owner: result.owner };
  });
  return { owners: resolved, options };
};

/*
  Rows saved before owners were stored as names hold "beneficiary:<id>" or
  "asset:<id>". Resolved to the current name so they do not show as raw ids; the
  next save rewrites them as names.
*/
export const resolveOwnerName = (stored, root) => {
  const data = root || {};
  if (typeof stored !== "string") return stored;
  if (stored.startsWith("beneficiary:")) {
    const id = stored.slice("beneficiary:".length);
    const person = (data.beneficiaries || []).find((b) => b.user_id === id);
    return person
      ? [person.name, person.last_name].filter(Boolean).join(" ").trim()
      : stored;
  }
  if (stored.startsWith("asset:")) {
    const id = stored.slice("asset:".length);
    const found = (data.assets || []).find(
      (a) => (a.id != null ? a.id : a.asset_id) === id
    );
    return found ? String(found.asset_name || "").trim() : stored;
  }
  return stored;
};

/* ------------------------------------------------------------- row order */
/*
  Manual row order.

  Each asset carries its own position, one field per table — `assets_order` for
  the Assets page and `asset_management_order` for Asset Management — so the two
  screens are ordered independently.

  The field did not exist before, so most assets have nothing in it. Rather than
  backfilling every account on deploy, an asset with no stored order falls back
  to its position in the array: an untouched account keeps exactly the order it
  has today, and values are only written once something is actually dragged.
  Ties break on that same position, which keeps the sort stable.
*/
export const ORDER_FIELDS = {
  assets: "assets_order",
  management: "asset_management_order",
};

export const rowIdOf = (asset, index) =>
  (asset && (asset.id || asset.asset_id)) || `row_${index}`;

const storedOrder = (asset, field) => {
  const raw = asset?.[field];
  if (raw === "" || raw === undefined || raw === null) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

// Sorted copy. Never mutates, and never drops an asset.
export const sortByStoredOrder = (assets, field) =>
  (Array.isArray(assets) ? assets : [])
    .map((asset, index) => ({ asset, index }))
    .sort((a, b) => {
      const ao = storedOrder(a.asset, field);
      const bo = storedOrder(b.asset, field);
      const av = ao === null ? a.index : ao;
      const bv = bo === null ? b.index : bo;
      return av === bv ? a.index - b.index : av - bv;
    })
    .map((entry) => entry.asset);

/*
  Moves one asset to another's position and returns the FULL asset list with a
  fresh contiguous order written onto every row.

  Every row is rewritten rather than only the ones that shifted: the values must
  stay contiguous or a later insert lands ambiguously, and writing them all is
  also what backfills the assets that never had an order. The list comes back in
  its original array order — only the order fields change — so callers can hand
  it straight to setUserData without disturbing anything else.
*/
export const reorderAssets = (assets, field, fromId, toId) => {
  const list = Array.isArray(assets) ? assets : [];
  if (!fromId || !toId || fromId === toId) return list;

  const sorted = sortByStoredOrder(list, field);
  const indexOfId = (id) =>
    sorted.findIndex((asset, i) => rowIdOf(asset, i) === id);

  // Positions are resolved against the sorted view, which is what the user sees.
  const from = indexOfId(fromId);
  if (from < 0) return list;
  const moved = sorted[from];
  const without = sorted.filter((_, i) => i !== from);
  const to = without.findIndex((asset, i) => rowIdOf(asset, i) === toId);
  without.splice(to < 0 ? without.length : to, 0, moved);

  const position = new Map();
  without.forEach((asset, i) => position.set(asset, i));
  return list.map((asset) => {
    const next = position.get(asset);
    return next === undefined ? asset : { ...asset, [field]: String(next) };
  });
};

/*
  Moves one asset one place up or down and returns the FULL list with a fresh
  contiguous order on every row, exactly as reorderAssets does.

  Separate from reorderAssets because dropping and stepping are different
  operations: a drop lands BEFORE the row it was dropped on, so "drop on my
  neighbour below" resolves to the position that row already vacated and nothing
  moves. Stepping says where to go outright.
*/
export const moveAssetBy = (assets, field, id, delta) => {
  const list = Array.isArray(assets) ? assets : [];
  if (!id || !delta) return list;

  const sorted = sortByStoredOrder(list, field);
  const from = sorted.findIndex((asset, i) => rowIdOf(asset, i) === id);
  const to = from + delta;
  // Already at the end it is being asked to move past.
  if (from < 0 || to < 0 || to >= sorted.length) return list;

  const next = [...sorted];
  next.splice(to, 0, next.splice(from, 1)[0]);

  const position = new Map();
  next.forEach((asset, i) => position.set(asset, i));
  return list.map((asset) => {
    const at = position.get(asset);
    return at === undefined ? asset : { ...asset, [field]: String(at) };
  });
};

/* --------------------------------------------------- custom asset types */
/*
  The built-in asset types, mirroring the <option> values in the Add / Edit
  Asset modals. Kept here so the dedupe below is comparing against the same list
  the dropdown offers.
*/
export const BUILT_IN_ASSET_TYPES = [
  "Bank Account",
  "Crypto Wallet",
  "Life Insurance",
  "Investment Account",
  "Company Shares",
  "Company",
  "NFT",
  "Password Manager",
  "Password",
  "Online storage",
  "Real Estate",
  "Retirement Plan",
  "Physical Vault",
  "Your Will",
  "Vehicle",
  "Precious Objects",
  "Business Ownership",
  "Loans Receivable",
  "Cash",
  "Intellectual Property",
  "Employee Benefits",
  "Other",
];

// The sentinel the dropdown uses to reveal the free-text field.
export const ASSET_TYPE_CUSTOM = "custom";

/*
  How tall the asset-type list may get inside the Add / Edit Asset modals
  before it scrolls.

  The native <select> popup sized itself to all twenty-odd types — it rendered
  about 820px and could not be capped, which is the reason for moving to
  `Dropdown` at all. This shows roughly eleven rows at a time.

  Deliberately taller than the Asset Management table's own cap: a modal has
  more room to work in than a table cell, and the list is the same length
  either way. The two are separate numbers on purpose.
*/
export const ASSET_TYPE_MENU_HEIGHT = 395;

/*
  The asset-type list in the shape `Dropdown` wants: one group with no heading,
  so it renders flat.

  Shared by Add Asset and Edit Asset, which each used to hard-code the same
  twenty-odd <option> elements. One list means a type added to
  BUILT_IN_ASSET_TYPES shows up in both without anyone remembering to.

  `translate` is passed in rather than imported so this file stays free of the
  language context — the caller already has it.

  A stored value that is neither built-in nor in the user's saved list (an
  older custom type) is appended, so the control shows it instead of rendering
  blank and losing it on the next save. That is what the trailing <option> in
  both modals used to do by hand.
*/
export const assetTypeGroupsFor = ({
  currentValue,
  customOptions,
  translate,
  customLabel,
}) => {
  // value === label for a custom type (see resolveAssetTypeSelection), so
  // either field identifies it.
  const saved = (Array.isArray(customOptions) ? customOptions : [])
    .map((option) => option?.value || option?.label)
    .filter(Boolean);

  const values = Array.from(
    new Set([...BUILT_IN_ASSET_TYPES, ...saved])
  ).filter(Boolean);

  const canonical = canonicalAssetType(currentValue);
  if (canonical && canonical !== ASSET_TYPE_CUSTOM && !values.includes(canonical)) {
    values.push(canonical);
  }

  return [
    {
      label: "",
      options: [
        ...values.map((value) => ({ value, label: translate(value) })),
        { value: ASSET_TYPE_CUSTOM, label: customLabel },
      ],
    },
  ];
};

/*
  Legacy stored values that are really built-in types under an older spelling.

  The Edit Asset dropdown used to save "online_storage" while Add Asset saved
  "Online storage", so both forms exist in real data — selectLogo already
  carries the same pair for the icon. Without this the underscored form would
  be treated as one of the user's own custom types, which would show it in the
  free-text field and, on save, add it to their custom list.
*/
const ASSET_TYPE_ALIASES = {
  online_storage: "Online storage",
};

/*
  The canonical built-in for a stored value: resolves a legacy alias, and any
  casing variant, to the exact string the dropdown offers. Anything genuinely
  unrecognised comes back unchanged — that is a real custom type.
*/
export const canonicalAssetType = (assetType) => {
  const value = String(assetType || "").trim();
  if (!value) return "";
  const alias = ASSET_TYPE_ALIASES[value.toLowerCase()];
  if (alias) return alias;
  const builtIn = BUILT_IN_ASSET_TYPES.find(
    (type) => type.toLowerCase() === value.toLowerCase()
  );
  return builtIn || value;
};

/*
  Resolves what `asset_type` should hold when the user picks Custom and types a
  name, creating a per-user option only when the name is genuinely new.

  Unlike the financial exposure, a custom asset type is stored as its LABEL
  rather than an id. That value is used directly to pick the icon (selectLogo
  falls through to a generic one for anything it does not recognise), to
  translate the cell (getAssetTypeTranslated returns the raw value when it has
  no key for it) and to filter the tables — an id would have to be resolved in
  all three places.

  Returns { asset_type, options }, options being the full list to persist.
*/
export const resolveAssetTypeSelection = (
  selected,
  typedLabel,
  existingOptions = []
) => {
  const options = Array.isArray(existingOptions) ? [...existingOptions] : [];

  if (selected !== ASSET_TYPE_CUSTOM) {
    // Canonicalised on the way through, so an asset saved under a legacy
    // spelling is quietly migrated the next time it is saved.
    return { asset_type: canonicalAssetType(selected), options };
  }

  const typed = String(typedLabel || "").trim();
  // Custom picked but nothing typed: nothing to store.
  if (!typed) return { asset_type: "", options };

  const key = typed.toLowerCase();

  // Typing an existing type selects it rather than duplicating it.
  const builtIn = BUILT_IN_ASSET_TYPES.find(
    (type) => type.toLowerCase() === key
  );
  if (builtIn) return { asset_type: builtIn, options };

  const existing = options.find(
    (option) => String(option?.label || "").trim().toLowerCase() === key
  );
  if (existing) return { asset_type: existing.label, options };

  // value === label: the asset stores the label, and the list is what the
  // dropdown offers, so the two are the same string.
  options.push({ value: typed, label: typed });
  return { asset_type: typed, options };
};

/*
  True when a stored asset_type is one of this user's own types rather than a
  built-in — which is what tells the modal to open on the free-text field.
*/
export const isCustomAssetType = (assetType) => {
  const value = String(assetType || "").trim();
  if (!value) return false;
  // Compared canonically, so a legacy alias or a casing variant of a built-in
  // is not mistaken for one of the user's own types.
  const canonical = canonicalAssetType(value);
  return !BUILT_IN_ASSET_TYPES.includes(canonical);
};

/*
  ---- what a debt has to say for itself ----

  Asked in two places: the Add / Edit Asset panel, and the debt modal the Asset
  Management table opens when a row has no debt yet. They used to hold a copy of
  the rule each, and the copies had to agree — the modal writes the debt, the
  panel validates it afterwards, so a panel that demanded more than the modal
  collected would refuse to save an asset the modal had just created, over
  fields the user had been told were optional. One rule, so that cannot happen.

  Of the three descriptive fields only the TYPE is required. A payoff date and a
  rate are often genuinely absent — a revolving credit line has no maturity, an
  overdraft no payoff date, a loan from family no interest — and demanding them
  meant a user who knew exactly what they owed could not record it at all. The
  amount then existed nowhere and was missing from total liabilities: refusing
  the figure did not produce a better one, it produced no figure.

  Neither is used in any calculation. A debt record carries an amount, a date
  and a currency; the description rides on the asset and is only ever displayed.
*/
const blank = (value) =>
  value === undefined || value === null || String(value).trim() === "";

/*
  The keys of the debt fields that still need filling in, as a Set.

  `amountRequired` is for the debt modal, which exists precisely to record an
  amount — saving without one would leave the user where they started. The panel
  leaves it off: there, a debt may be described now and measured later, the same
  way an asset may be described before it is valued.
*/
export const missingDebtFields = (debt, { amountRequired = false } = {}) => {
  const missing = new Set();
  if (!debt) return missing;

  const describing = [
    debt.debt_type,
    debt.debt_payoff_date,
    debt.debt_annual_interest,
  ].some((value) => !blank(value));
  const recording = amountRequired || !blank(debt.debt_amount);

  /*
    Touching any descriptive field pulls the type in with it. That is the
    half-a-debt guard worth keeping: a payoff date on an asset with no debt type
    describes nothing at all.
  */
  if ((describing || recording) && blank(debt.debt_type)) missing.add("debt_type");
  if (recording) {
    if (blank(debt.debt_amount)) missing.add("debt_amount");
    if (blank(debt.debt_currency)) missing.add("debt_currency");
  }
  return missing;
};
